import { useState, useEffect, useCallback } from 'react';
import { schoolApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Loader2, School } from 'lucide-react';
import { toast } from 'sonner';

export default function SchoolClassesPage() {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', teacher_id: '', class_type: 'quranic' });

  const loadData = useCallback(async () => {
    try {
      const [classesRes, teachersRes] = await Promise.all([schoolApi.getClasses(), schoolApi.getTeachers()]);
      setClasses(classesRes.data);
      setTeachers(teachersRes.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schoolApi.createClass(form);
      toast.success('Classe creee');
      setDialogOpen(false);
      setForm({ name: '', teacher_id: '', class_type: 'quranic' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try { await schoolApi.deleteClass(id); toast.success('Supprimee'); loadData(); } catch { toast.error('Erreur'); }
  };

  const getTeacherName = (id) => { const t = teachers.find(t => t.id === id); return t ? `${t.first_name} ${t.last_name}` : '-'; };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Classes</h1>
          <p className="text-sm text-stone-500">{classes.length} classe(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-class-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle classe</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="class-name" /></div>
              <div>
                <Label>Enseignant</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="class-teacher"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.class_type} onValueChange={(v) => setForm({ ...form, class_type: v })}>
                  <SelectTrigger data-testid="class-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quranic">Coranique</SelectItem>
                    <SelectItem value="classical">Classique</SelectItem>
                    <SelectItem value="both">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="class-submit">Creer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : classes.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-stone-400">Aucune classe</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map(c => (
            <Card key={c.id} className="border-stone-200" data-testid={`class-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><School className="w-4 h-4 text-violet-600" /></div>
                    <h3 className="font-semibold text-stone-900 text-sm">{c.name}</h3>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id, c.name)} className="h-7 w-7 p-0 text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-xs text-stone-500">Enseignant: {getTeacherName(c.teacher_id)}</p>
                <p className="text-xs text-stone-400 mt-0.5">Type: {c.class_type}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
